import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, CheckCircle } from 'lucide-react';
import { authConfig } from '../../config/auth';
import LandingNav from '../LandingNav';
import '../../styles/auth.css';
import '../../styles/landing.css';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = (location.state as { email?: string })?.email || '';
  const [email, setEmail] = useState(emailFromState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${authConfig.apiUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to send reset code');
        setLoading(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/reset-password', { state: { email } });
      }, 2000);
    } catch (err) {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="landing-page">
      <LandingNav />

      {/* Auth Content */}
      <div className="auth-container">
        <div className="auth-header">
          <p className="auth-header-subtitle">Reset Your Password</p>
        </div>

        <div className="auth-card">
          <h2 className="auth-card-title">Forgot Password?</h2>
          <p className="text-center info-text" style={{ marginBottom: '24px' }}>
            Enter your email and we'll send you a reset code
          </p>

          {success ? (
            <div className="success-message">
              <div className="success-icon">
                <CheckCircle size={32} color="#00bf62" />
              </div>
              <div className="success-text">
                If an account exists with this email, a reset code has been sent.
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="input-container">
                <span className="input-icon">
                  <Mail size={18} />
                </span>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={loading}
                  className="input-field"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="auth-button"
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>

              <div className="text-center mt-sm">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="auth-link"
                >
                  ← Back to Login
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
